import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/CookieConsent.css';

const STORAGE_KEY = 'cookie-consent';

const defaultPreferences = {
  essential: true,   // always on, cannot be disabled
  analytics: false,
  marketing: false,
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setIsVisible(true);
    }
  }, []);

  const saveConsent = (consent) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...consent,
      timestamp: new Date().toISOString(),
    }));
    setIsVisible(false);
    setShowPreferences(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ essential: true, analytics: true, marketing: true });
  };

  const handleDeclineAll = () => {
    saveConsent({ essential: true, analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // cannot toggle essential cookies off
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="cookie-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="cookie-banner-inner">

            {!showPreferences ? (
              <>
                <div className="cookie-banner-content">
                  <div className="cookie-banner-text">
                    <h3>We value your privacy</h3>
                    <p>
                      We use cookies to keep the site running smoothly, understand how it's used, and improve our service.
                      You can accept all cookies, decline non-essential ones, or manage your preferences.
                      Read our{' '}
                      <Link to="/cookies" className="cookie-banner-link">Cookie Policy</Link>{' '}
                      for more details.
                    </p>
                  </div>
                </div>

                <div className="cookie-banner-actions">
                  <button
                    className="cookie-btn cookie-btn-ghost"
                    onClick={() => setShowPreferences(true)}
                  >
                    <Settings size={15} />
                    Manage preferences
                  </button>
                  <button
                    className="cookie-btn cookie-btn-outline"
                    onClick={handleDeclineAll}
                  >
                    Decline
                  </button>
                  <button
                    className="cookie-btn cookie-btn-primary"
                    onClick={handleAcceptAll}
                  >
                    Accept all
                  </button>
                </div>
              </>
            ) : (
              <div className="cookie-prefs">
                <div className="cookie-prefs-header">
                  <h3>Manage cookie preferences</h3>
                  <button
                    className="cookie-prefs-close"
                    onClick={() => setShowPreferences(false)}
                    aria-label="Back"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="cookie-prefs-list">
                  <div className="cookie-pref-row">
                    <div className="cookie-pref-info">
                      <h4>Essential</h4>
                      <p>Required for the site to function — login, security, and basic navigation. Cannot be disabled.</p>
                    </div>
                    <div className="cookie-toggle cookie-toggle-locked" aria-disabled="true">
                      <div className="cookie-toggle-track">
                        <div className="cookie-toggle-thumb" />
                      </div>
                    </div>
                  </div>

                  <div className="cookie-pref-row">
                    <div className="cookie-pref-info">
                      <h4>Analytics</h4>
                      <p>Helps us understand how visitors use the site so we can improve it.</p>
                    </div>
                    <button
                      className={`cookie-toggle ${preferences.analytics ? 'cookie-toggle-on' : ''}`}
                      onClick={() => togglePreference('analytics')}
                      role="switch"
                      aria-checked={preferences.analytics}
                      aria-label="Toggle analytics cookies"
                    >
                      <div className="cookie-toggle-track">
                        <div className="cookie-toggle-thumb" />
                      </div>
                    </button>
                  </div>

                  <div className="cookie-pref-row">
                    <div className="cookie-pref-info">
                      <h4>Marketing</h4>
                      <p>Used to show relevant offers and measure marketing performance.</p>
                    </div>
                    <button
                      className={`cookie-toggle ${preferences.marketing ? 'cookie-toggle-on' : ''}`}
                      onClick={() => togglePreference('marketing')}
                      role="switch"
                      aria-checked={preferences.marketing}
                      aria-label="Toggle marketing cookies"
                    >
                      <div className="cookie-toggle-track">
                        <div className="cookie-toggle-thumb" />
                      </div>
                    </button>
                  </div>
                </div>

                <div className="cookie-prefs-actions">
                  <button
                    className="cookie-btn cookie-btn-outline"
                    onClick={handleDeclineAll}
                  >
                    Decline all
                  </button>
                  <button
                    className="cookie-btn cookie-btn-primary"
                    onClick={handleSavePreferences}
                  >
                    Save preferences
                  </button>
                </div>
              </div>
            )}

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;